import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/** Mesai saatleri (TR); dışında sinyal gelmemesi sorun sayılmaz. Kural API'de. */
export const OPEN_HOURS_LABEL = "08:00–22:00";

export type PrizmaScreenStatus = "online" | "silent" | "notOpened" | "offHours";

export const STATUS_LABELS: Record<PrizmaScreenStatus, string> = {
  online: "Sinyal var",
  silent: "Sinyal yok",
  notOpened: "Bugün açılmadı",
  offHours: "Mesai dışı",
};

export const STATUS_COLORS: Record<PrizmaScreenStatus, string> = {
  online: "green",
  silent: "red",
  notOpened: "red",
  offHours: "default",
};

// GET /api/analytics/prizma/screens yanıtındaki bir ekran (ekranim-api PrizmaMonitorService.getScreens)
export type PrizmaScreen = {
  deviceUuid: string;
  ommaDeviceId: string | number | null;
  screenId: number | null;
  screen: string;
  build: string | null;
  shopName: string | null;
  shopCode: string | null;
  shopMatched: boolean;
  shop: {
    name: string;
    city: string | null;
    area: string | null;
    salesManager: string | null;
    areaManager: string | null;
    isActive: boolean;
  } | null;
  /**
   * Mesai saatinde 15 dakikadan uzun sinyal yoksa: bugün sinyal geldiyse "silent",
   * hiç gelmediyse "notOpened". Mesai dışında sinyal gelmeyen ekran "offHours".
   */
  status: PrizmaScreenStatus;
  /** Son 15 dakikada sinyal geldi mi */
  online: boolean;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  sessionStartedAt: string | null;
  lastTouchAt: string | null;
  /** Son dokunmadan beri ekranın mesai saatinde açık kaldığı süre (22:00–08:00 sayılmaz) */
  openSecSinceLastTouch: number;
  touchStreak: number;
  video: {
    readyState: number;
    paused: boolean;
    error: number | null;
    currentTime: number;
  } | null;
  /** Video bir önceki sinyale göre ilerlemiş mi; karşılaştırılamıyorsa null */
  videoAdvancing: boolean | null;
  lastGap: {
    kind: "connection" | "restart";
    from: string | null;
    to: string | null;
  } | null;
  today: {
    /** İstanbul günü (YYYY-MM-DD) */
    date: string;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    openMinutes: number;
    touches: number;
    popupOpens: number;
    restarts: number;
  };
  diagnostics: Record<string, unknown> | null;
};

export const GAP_KIND_LABELS = {
  connection: "Bağlantı",
  restart: "Yeniden başlama",
};

export function formatDateTime(iso?: string | null): string {
  return iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "-";
}

export function formatFromNow(iso?: string | null): string {
  return iso ? dayjs(iso).fromNow() : "-";
}

export function formatDuration(totalSec?: number | null): string {
  if (totalSec === null || totalSec === undefined) return "-";
  const totalMinutes = Math.floor(totalSec / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} g ${hours} sa`;
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  return `${minutes} dk`;
}

export function formatVideo(screen: PrizmaScreen): string {
  const { video, videoAdvancing } = screen;
  if (!video) return "-";
  if (video.error !== null) return `Hata (${video.error})`;
  if (video.paused) return "Duraklatılmış";
  if (videoAdvancing === null) return "—";
  return videoAdvancing ? "Oynuyor" : "İlerlemiyor";
}

export function shopLabel(screen: PrizmaScreen): string {
  return screen.shop ? screen.shop.name : screen.shopName || "-";
}

export function screenLabel(screen: PrizmaScreen): string {
  return screen.screenId
    ? `${screen.screen} (${screen.screenId})`
    : screen.screen;
}
